/**
 * About Page API services
 */

import { fetchStrapi, getStrapiMediaUrl } from './strapi';

// === TYPES ===

export interface AboutHero {
  heading: string;
  intro: string;
  description: string;
  subtext: string;
  imageUrl: string;
}

export interface AboutPhilosophy {
  heading: string;
  text: string;
  secondaryText: string;
  tagline: string;
}

export interface ExpertiseArea {
  text: string;
}

export interface WorldCard {
  icon: string;
  title: string;
  description: string;
  bulletPoints: ExpertiseArea[];
  colorTheme: 'red' | 'green';
}

export interface AboutTwoWorlds {
  heading: string;
  subheading: string;
  businessCard: WorldCard | null;
  spiritualCard: WorldCard | null;
  synergyHeading: string;
  synergyText: string;
  synergySubtext: string;
}

export interface AboutValue {
  icon: string;
  title: string;
  description: string;
}

export interface AboutValues {
  heading: string;
  subheading: string;
  values: AboutValue[];
}

export interface AboutMilestone {
  year: string;
  title: string;
  description: string;
}

export interface AboutJourney {
  heading: string;
  subheading: string;
  milestones: AboutMilestone[];
}

export interface ExpertiseCategory {
  category: string;
  areas: ExpertiseArea[];
}

export interface AboutExpertise {
  heading: string;
  subheading: string;
  categories: ExpertiseCategory[];
}

export interface Qualification {
  icon: string;
  text: string;
}

export interface AboutQualifications {
  heading: string;
  subheading: string;
  qualifications: Qualification[];
}

export interface AboutCta {
  heading: string;
  description: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
}

// === FETCH FUNCTIONS ===

export async function fetchAboutHero(): Promise<AboutHero | null> {
  try {
    const response = await fetchStrapi<any>('/about-hero?populate=*');
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      intro: data.einleitung || '',
      description: data.beschreibung || '',
      subtext: data.zusatztext || '',
      imageUrl: data.bild?.url ? getStrapiMediaUrl(data.bild.url) : '',
    };
  } catch (error) {
    console.error('Error fetching about hero:', error);
    return null;
  }
}

export async function fetchAboutPhilosophy(): Promise<AboutPhilosophy | null> {
  try {
    const response = await fetchStrapi<any>('/about-philosophy');
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      text: data.text || '',
      secondaryText: data.sekundaererText || '',
      tagline: data.tagline || '',
    };
  } catch (error) {
    console.error('Error fetching about philosophy:', error);
    return null;
  }
}

export async function fetchAboutTwoWorlds(): Promise<AboutTwoWorlds | null> {
  try {
    const response = await fetchStrapi<any>(
      '/about-two-worlds?populate[businessKarte][populate]=aufzaehlungen&populate[spirituelleKarte][populate]=aufzaehlungen'
    );
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      subheading: data.unterueberschrift || '',
      businessCard: data.businessKarte
        ? {
            icon: data.businessKarte.icon,
            title: data.businessKarte.titel,
            description: data.businessKarte.beschreibung,
            bulletPoints: (data.businessKarte.aufzaehlungen || []).map((a: any) => ({ text: a.text })),
            colorTheme: data.businessKarte.farbThema,
          }
        : null,
      spiritualCard: data.spirituelleKarte
        ? {
            icon: data.spirituelleKarte.icon,
            title: data.spirituelleKarte.titel,
            description: data.spirituelleKarte.beschreibung,
            bulletPoints: (data.spirituelleKarte.aufzaehlungen || []).map((a: any) => ({ text: a.text })),
            colorTheme: data.spirituelleKarte.farbThema,
          }
        : null,
      synergyHeading: data.synergieUeberschrift || '',
      synergyText: data.synergieText || '',
      synergySubtext: data.synergieZusatztext || '',
    };
  } catch (error) {
    console.error('Error fetching about two worlds:', error);
    return null;
  }
}

export async function fetchAboutValues(): Promise<AboutValues | null> {
  try {
    const response = await fetchStrapi<any>('/about-values?populate[werte]=*');
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      subheading: data.unterueberschrift || '',
      values: (data.werte || []).map((w: any) => ({
        icon: w.icon,
        title: w.titel,
        description: w.beschreibung,
      })),
    };
  } catch (error) {
    console.error('Error fetching about values:', error);
    return null;
  }
}

export async function fetchAboutJourney(): Promise<AboutJourney | null> {
  try {
    const response = await fetchStrapi<any>('/about-journey?populate[meilensteine]=*');
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      subheading: data.unterueberschrift || '',
      milestones: (data.meilensteine || []).map((m: any) => ({
        year: m.jahr,
        title: m.titel,
        description: m.beschreibung,
      })),
    };
  } catch (error) {
    console.error('Error fetching about journey:', error);
    return null;
  }
}

export async function fetchAboutExpertise(): Promise<AboutExpertise | null> {
  try {
    const response = await fetchStrapi<any>(
      '/about-expertise?populate[kategorien][populate]=bereiche'
    );
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      subheading: data.unterueberschrift || '',
      categories: (data.kategorien || []).map((k: any) => ({
        category: k.kategorie,
        areas: (k.bereiche || []).map((b: any) => ({ text: b.text })),
      })),
    };
  } catch (error) {
    console.error('Error fetching about expertise:', error);
    return null;
  }
}

export async function fetchAboutQualifications(): Promise<AboutQualifications | null> {
  try {
    const response = await fetchStrapi<any>('/about-qualifications?populate[qualifikationen]=*');
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      subheading: data.unterueberschrift || '',
      qualifications: (data.qualifikationen || []).map((q: any) => ({
        icon: q.icon,
        text: q.text,
      })),
    };
  } catch (error) {
    console.error('Error fetching about qualifications:', error);
    return null;
  }
}

export async function fetchAboutCta(): Promise<AboutCta | null> {
  try {
    const response = await fetchStrapi<any>('/about-cta');
    if (!response.data) return null;
    const data = response.data;
    return {
      heading: data.ueberschrift || '',
      description: data.beschreibung || '',
      primaryButtonText: data.primaerButtonText || '',
      primaryButtonLink: data.primaerButtonLink || '',
      secondaryButtonText: data.sekundaerButtonText || '',
      secondaryButtonLink: data.sekundaerButtonLink || '',
    };
  } catch (error) {
    console.error('Error fetching about cta:', error);
    return null;
  }
}
